import { useQuery } from "@tanstack/react-query";

import {
  IEMedIntegrationCount,
  IEMedPatientLabResults,
  IEMedPatientMedications,
} from "../../../@types/admin/AdminEMedIntegration";
import { eMedIntegrationService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/** Integration counts by date — the page's list query. */
export function useEMedIntegrationCounts() {
  const { data, isLoading, isError } = useQuery<IEMedIntegrationCount[]>({
    queryKey: ["admin-emed-integration-counts"],
    queryFn: async () =>
      ensureOk(await eMedIntegrationService.getIntegrationCounts()),
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return { integrationCounts: data ?? [], isLoading, isError };
}

/**
 * Lab results for one date. Idle until a date is chosen — these reads are
 * slow enough that Retool gave them a 100-second timeout, so nothing is
 * fetched until the operator opens the drill-down.
 */
export function useEMedLabResults(date: string | undefined) {
  const { data, isLoading, isError } = useQuery<IEMedPatientLabResults[]>({
    queryKey: ["admin-emed-lab-results", date],
    queryFn: async () =>
      ensureOk(await eMedIntegrationService.getPatientLabResultsByDate(date!)),
    enabled: Boolean(date),
  });

  return { labResults: data ?? [], isLoading, isError };
}

/** Medications for one date. Idle until a date is chosen. */
export function useEMedMedications(date: string | undefined) {
  const { data, isLoading, isError } = useQuery<IEMedPatientMedications[]>({
    queryKey: ["admin-emed-medications", date],
    queryFn: async () =>
      ensureOk(await eMedIntegrationService.getPatientMedicationsByDate(date!)),
    enabled: Boolean(date),
  });

  return { medications: data ?? [], isLoading, isError };
}
